import { useCallback } from "react";
import { Link } from "react-router-dom";

interface QuickLinkCard {
  href: string;
  icon: string;
  label: string;
  description: string;
  actionText: string;
}

const cards: QuickLinkCard[] = [
  {
    href: "/#member",
    icon: "ri-shield-user-line",
    label: "Member Portal",
    description:
      "Learn how to access your secure member portal and pension account information.",
    actionText: "Portal access",
  },
  {
    href: "/resources",
    icon: "ri-file-list-3-line",
    label: "Resources & Forms",
    description:
      "Find annual reports, newsletters, forms, notices, and reference materials.",
    actionText: "View resources",
  },
  {
    href: "/news",
    icon: "ri-newspaper-line",
    label: "News & Updates",
    description:
      "Read the latest announcements, notices, and updates from the Fund.",
    actionText: "View news",
  },
  {
    href: "/faq",
    icon: "ri-question-answer-line",
    label: "FAQ",
    description:
      "Find answers to common questions about contributions, pension benefits, and the Fund.",
    actionText: "View FAQ",
  },
];

function CardContent({ card }: { card: QuickLinkCard }) {
  return (
    <>
      {/* Icon area — larger and with visible accent background */}
      <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#e8f4fb] mb-5 transition-colors duration-200 group-hover:bg-[#d0eaf7]">
        <i className={`${card.icon} text-[22px] text-[#1a8fc1]`}></i>
      </div>

      {/* Title */}
      <h3 className="text-base font-semibold text-[#0d1f4c] mb-2 group-hover:text-[#1a8fc1] transition-colors duration-200">
        {card.label}
      </h3>

      {/* Description */}
      <p className="text-[13px] text-gray-500 leading-relaxed mb-5">
        {card.description}
      </p>

      {/* CTA link */}
      <div className="flex items-center gap-1.5 text-[#1a8fc1] text-sm font-medium mt-auto">
        <span className="whitespace-nowrap">{card.actionText}</span>
        <i className="ri-arrow-right-line text-sm transition-transform duration-200 group-hover:translate-x-1"></i>
      </div>
    </>
  );
}

export default function QuickLinks() {
  const handleMemberScroll = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const el = document.getElementById("member");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 56;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  return (
    <section className="w-full bg-[#f4f8fc] py-14">
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((card) => {
            const isHashLink = card.href.startsWith("/#");

            if (isHashLink) {
              return (
                <a
                  key={card.label}
                  href={card.href}
                  onClick={handleMemberScroll}
                  className="group flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:border-[#1a8fc1]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
                >
                  <CardContent card={card} />
                </a>
              );
            }

            return (
              <Link
                key={card.label}
                to={card.href}
                className="group flex flex-col bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:border-[#1a8fc1]/50 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
              >
                <CardContent card={card} />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}